import { Controller } from "@hotwired/stimulus"
import { EditorView, basicSetup } from "codemirror"
import { EditorState } from "@codemirror/state"
import { json } from "@codemirror/lang-json"
import { linter, lintGutter } from "@codemirror/lint"
import { ViewUpdate } from "@codemirror/view"

const VALID_SCORES = [1, 2, 4, 8]
const VALID_TSHIRT_LABELS = ["S", "M", "L", "XL", "XXL"]
const HEX_COLOR_RE = /^#[0-9a-fA-F]{6}$/
const KEY_FORMAT_RE = /^[a-z_]+$/

function validateSchema(view) {
  const doc = view.state.doc.toString()
  const diagnostics = []

  let data
  try {
    data = JSON.parse(doc)
  } catch (e) {
    const pos = e.message.match(/position (\d+)/)
    const from = pos ? parseInt(pos[1]) : 0
    diagnostics.push({
      from,
      to: Math.min(from + 1, doc.length),
      severity: "error",
      message: "JSON invalide : " + e.message
    })
    return diagnostics
  }

  function addError(msg) {
    diagnostics.push({ from: 0, to: doc.length, severity: "error", message: msg })
  }

  if (typeof data !== "object" || Array.isArray(data) || data === null) {
    addError("Le JSON doit être un objet avec les clés \"axes\" et \"thresholds\"")
    return diagnostics
  }

  if (!Array.isArray(data.axes)) {
    addError("\"axes\" est requis et doit être un tableau")
  } else {
    data.axes.forEach((axis, i) => {
      const prefix = `axes[${i}]`
      if (!axis.key || typeof axis.key !== "string") {
        addError(`${prefix}.key est requis (string)`)
      } else if (!KEY_FORMAT_RE.test(axis.key)) {
        addError(`${prefix}.key doit être en snake_case (lettres minuscules et underscores)`)
      }
      if (!axis.name || typeof axis.name !== "string") {
        addError(`${prefix}.name est requis (string)`)
      }
      if (typeof axis.weight !== "number" || axis.weight < 0.1 || axis.weight > 5.0) {
        addError(`${prefix}.weight doit être un nombre entre 0.1 et 5.0`)
      }
      if (axis.position !== undefined && (!Number.isInteger(axis.position) || axis.position < 1)) {
        addError(`${prefix}.position doit être un entier positif`)
      }
      if (!Array.isArray(axis.levels) || axis.levels.length < 2) {
        addError(`${prefix}.levels doit contenir au moins 2 éléments`)
      } else {
        axis.levels.forEach((level, j) => {
          const lp = `${prefix}.levels[${j}]`
          if (!VALID_SCORES.includes(level.score)) {
            addError(`${lp}.score doit être 1, 2, 4 ou 8`)
          }
          if (!level.label || typeof level.label !== "string") {
            addError(`${lp}.label est requis (string)`)
          }
        })
      }
    })
  }

  if (!Array.isArray(data.thresholds)) {
    addError("\"thresholds\" est requis et doit être un tableau")
  } else {
    data.thresholds.forEach((t, i) => {
      const prefix = `thresholds[${i}]`
      if (!VALID_TSHIRT_LABELS.includes(t.label)) {
        addError(`${prefix}.label doit être S, M, L, XL ou XXL`)
      }
      if (typeof t.max_percentage !== "number" || t.max_percentage <= 0 || t.max_percentage > 1) {
        addError(`${prefix}.max_percentage doit être un nombre entre 0 (exclu) et 1`)
      }
      if (!t.color || !HEX_COLOR_RE.test(t.color)) {
        addError(`${prefix}.color doit être au format #hex6 (ex: #22c55e)`)
      }
      if (t.position !== undefined && (!Number.isInteger(t.position) || t.position < 1)) {
        addError(`${prefix}.position doit être un entier positif`)
      }
    })
  }

  return diagnostics
}

export default class extends Controller {
  static targets = ["container", "hiddenInput", "errors", "submitButton", "previewPanel", "previewButton"]
  static values = { initialValue: String, readonly: Boolean, previewUrl: String }

  connect() {
    const jsonLinter = linter(validateSchema)

    const extensions = [
      basicSetup,
      json(),
      lintGutter(),
      jsonLinter,
      EditorView.theme({
        "&": { fontSize: "13px" },
        ".cm-editor": { borderRadius: "0.5rem", border: "1px solid #d1d5db" },
        ".cm-scroller": { minHeight: "400px" }
      })
    ]

    if (this.readonlyValue) {
      extensions.push(EditorState.readOnly.of(true))
      extensions.push(EditorView.editable.of(false))
    } else {
      extensions.push(
        EditorView.updateListener.of((update) => {
          if (update.docChanged) {
            this.onDocChange(update)
          }
        })
      )
    }

    let initialJson = this.initialValueValue
    try {
      initialJson = JSON.stringify(JSON.parse(initialJson), null, 2)
    } catch {
      // keep as-is
    }

    this.editor = new EditorView({
      state: EditorState.create({
        doc: initialJson,
        extensions
      }),
      parent: this.containerTarget
    })

    if (!this.readonlyValue) {
      this.syncHiddenInput()
    }
  }

  onDocChange(update) {
    const content = this.editor.state.doc.toString()
    this.syncHiddenInput()

    let isValid = false
    try {
      const data = JSON.parse(content)
      const errors = validateSchema(this.editor)
      isValid = errors.length === 0
      this.showErrors(errors)
    } catch {
      isValid = false
      this.showErrors([{ message: "JSON invalide" }])
    }

    if (this.hasSubmitButtonTarget) {
      this.submitButtonTarget.disabled = !isValid
      if (isValid) {
        this.submitButtonTarget.classList.remove("opacity-50", "cursor-not-allowed")
      } else {
        this.submitButtonTarget.classList.add("opacity-50", "cursor-not-allowed")
      }
    }
  }

  syncHiddenInput() {
    if (this.hasHiddenInputTarget) {
      this.hiddenInputTarget.value = this.editor.state.doc.toString()
    }
  }

  showErrors(errors) {
    if (!this.hasErrorsTarget) return

    if (errors.length === 0) {
      this.errorsTarget.innerHTML = ""
      this.errorsTarget.classList.add("hidden")
    } else {
      this.errorsTarget.classList.remove("hidden")
      this.errorsTarget.innerHTML = errors
        .map((e) => `<div class="text-sm text-red-600">${this.escapeHtml(e.message)}</div>`)
        .join("")
    }
  }

  escapeHtml(str) {
    const div = document.createElement("div")
    div.textContent = str
    return div.innerHTML
  }

  async preview() {
    const content = this.editor.state.doc.toString()
    try { JSON.parse(content) } catch { return }

    const csrfToken = document.querySelector('meta[name="csrf-token"]').content
    const response = await fetch(this.previewUrlValue, {
      method: "POST",
      headers: {
        "Content-Type": "application/x-www-form-urlencoded",
        "X-CSRF-Token": csrfToken,
        "Accept": "text/html"
      },
      body: new URLSearchParams({ json_content: content })
    })

    if (response.ok) {
      this.previewPanelTarget.innerHTML = await response.text()
    }
  }

  disconnect() {
    if (this.editor) {
      this.editor.destroy()
    }
  }
}